import type {
  PromotionAttributionContext,
  PromotionEventType,
  PromotionTargetType,
} from '../types/promotion-kit';

const PROMOTION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22,64}$/;
const ATTRIBUTION_TTL_MS = 24 * 60 * 60 * 1000;

function hasControlOrBidi(value: string): boolean {
  return [...value].some((char) => {
    const code = char.codePointAt(0) ?? 0;
    return (
      code <= 0x1f ||
      code === 0x7f ||
      (code >= 0x202a && code <= 0x202e) ||
      (code >= 0x2066 && code <= 0x2069)
    );
  });
}

export function isPromotionTargetType(value: string): value is PromotionTargetType {
  return [
    'store_home',
    'product',
    'category',
    'live_offer_campaign',
    'sales_page',
    'review_highlight',
  ].includes(value);
}

export function isPromotionEventType(value: string): value is PromotionEventType {
  return [
    'link_visit',
    'product_detail_view',
    'contact_click',
    'add_to_cart_click',
    'order_start_click',
  ].includes(value);
}

export function isValidPromotionToken(value: string | null | undefined): value is string {
  return typeof value === 'string' && PROMOTION_TOKEN_PATTERN.test(value);
}

function assertSafePathSegment(value: string, fieldName: string): string {
  if (!value || hasControlOrBidi(value) || value.includes('/')) {
    throw new Error(`Invalid promotion ${fieldName}`);
  }
  return encodeURIComponent(value);
}

export function buildPromotionPublicPath(input: {
  readonly storeSlug: string;
  readonly targetType: PromotionTargetType;
  readonly targetId?: string | null;
  readonly productId?: string | null;
  readonly campaignSlug?: string | null;
}): string {
  const slug = assertSafePathSegment(input.storeSlug, 'storeSlug');
  if (input.targetType === 'store_home') return `/s/${slug}`;
  if (input.targetType === 'sales_page') return `/s/${slug}/sales`;
  if (input.targetType === 'product') {
    return `/s/${slug}/p/${assertSafePathSegment(input.targetId ?? '', 'targetId')}`;
  }
  if (input.targetType === 'category') {
    return `/s/${slug}/c/${assertSafePathSegment(input.targetId ?? '', 'targetId')}`;
  }
  if (input.targetType === 'live_offer_campaign') {
    return `/s/${slug}/offers/${assertSafePathSegment(input.campaignSlug ?? '', 'campaignSlug')}`;
  }
  return `/s/${slug}/p/${assertSafePathSegment(input.productId ?? '', 'productId')}?review=${assertSafePathSegment(
    input.targetId ?? '',
    'targetId',
  )}#product-reviews`;
}

export function readPromotionToken(searchParams: URLSearchParams): string | null {
  const token = searchParams.get('pk');
  return isValidPromotionToken(token) ? token : null;
}

export function stripPromotionParams(urlOrPath: string): string {
  const url = new URL(urlOrPath, 'https://dukanti.local');
  url.searchParams.delete('pk');
  url.searchParams.delete('utm_source');
  const query = url.searchParams.toString();
  return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
}

function storageKey(storeSlug: string): string {
  return `dukanti:promotion-attribution:${storeSlug}`;
}

function removeStoredContext(storeSlug: string, storage?: Storage): void {
  try {
    storage?.removeItem(storageKey(storeSlug));
  } catch {
    // Attribution cleanup is best effort when browser storage is unavailable.
  }
}

function readStoredContext(
  storeSlug: string,
  now: Date,
  storage?: Storage,
): PromotionAttributionContext | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey(storeSlug));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      removeStoredContext(storeSlug, storage);
      return null;
    }
    const context = parsed as Record<string, unknown>;
    if (
      typeof context.token !== 'string' ||
      !isValidPromotionToken(context.token) ||
      context.storeSlug !== storeSlug ||
      typeof context.firstSeenAt !== 'string' ||
      typeof context.lastSeenAt !== 'string'
    ) {
      removeStoredContext(storeSlug, storage);
      return null;
    }
    const firstSeenAt = Date.parse(context.firstSeenAt);
    const lastSeenAt = Date.parse(context.lastSeenAt);
    const nowMs = now.getTime();
    if (
      !Number.isFinite(firstSeenAt) ||
      !Number.isFinite(lastSeenAt) ||
      firstSeenAt > lastSeenAt ||
      lastSeenAt > nowMs ||
      nowMs - firstSeenAt > ATTRIBUTION_TTL_MS
    ) {
      removeStoredContext(storeSlug, storage);
      return null;
    }
    return {
      token: context.token,
      storeSlug,
      firstSeenAt: context.firstSeenAt,
      lastSeenAt: context.lastSeenAt,
    };
  } catch {
    removeStoredContext(storeSlug, storage);
    return null;
  }
}

export function readPromotionAttributionContext(input: {
  readonly storeSlug: string;
  readonly searchParams: URLSearchParams;
  readonly now?: Date;
  readonly storage?: Storage;
}): PromotionAttributionContext | null {
  const now = input.now ?? new Date();
  if (!Number.isFinite(now.getTime())) return null;
  const token = readPromotionToken(input.searchParams);
  if (token) {
    const previous = readStoredContext(input.storeSlug, now, input.storage);
    const context: PromotionAttributionContext = {
      token,
      storeSlug: input.storeSlug,
      firstSeenAt: previous?.token === token ? previous.firstSeenAt : now.toISOString(),
      lastSeenAt: now.toISOString(),
    };
    writePromotionAttributionContext({ context, storage: input.storage });
    return context;
  }
  return readStoredContext(input.storeSlug, now, input.storage);
}

export function writePromotionAttributionContext(input: {
  readonly context: PromotionAttributionContext;
  readonly storage?: Storage;
}): void {
  if (!input.storage || !isValidPromotionToken(input.context.token)) return;
  try {
    input.storage.setItem(storageKey(input.context.storeSlug), JSON.stringify(input.context));
  } catch {
    // Attribution persistence must not interrupt navigation or commerce.
  }
}

export function clearPromotionAttributionContext(input: {
  readonly storeSlug: string;
  readonly storage?: Storage;
}): void {
  removeStoredContext(input.storeSlug, input.storage);
}
